import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';

/**
 * Hook personalizado para manejar permisos de usuario
 * Proporciona funciones para verificar permisos específicos
 */
export const usePermissions = () => {
    const { user } = useApp();
    const [permissions, setPermissions] = useState([]);

    useEffect(() => {
        // Obtener permisos del usuario actual
        if (user && user.permisos) {
            setPermissions(user.permisos);
        } else {
            // Fallback: obtener del localStorage
            const savedPermissions = localStorage.getItem('userPermissions');
            if (savedPermissions) {
                try {
                    const parsedPermissions = JSON.parse(savedPermissions);
                    setPermissions(parsedPermissions);
                } catch (e) {
                    console.warn('Error parsing permissions from localStorage:', e);
                    setPermissions([]);
                }
            } else {
                setPermissions([]);
            }
        }
    }, [user]);

    /**
     * Verifica si el usuario tiene un permiso específico
     * @param {string} permission - Nombre del permiso (ej: 'usuarios_ver')
     * @returns {boolean}
     */
    const hasPermission = (permission) => {
        return permissions.includes(permission);
    };

    /**
     * Verifica si el usuario tiene al menos uno de los permisos especificados
     * @param {string[]} permissionList - Array de permisos
     * @returns {boolean}
     */
    const hasAnyPermission = (permissionList) => {
        return permissionList.some(permission => permissions.includes(permission));
    };

    /**
     * Verifica si el usuario tiene todos los permisos especificados
     * @param {string[]} permissionList - Array de permisos
     * @returns {boolean}
     */
    const hasAllPermissions = (permissionList) => {
        return permissionList.every(permission => permissions.includes(permission));
    };

    /**
     * Verifica permisos por módulo
     * @param {string} module - Nombre del módulo (ej: 'usuarios')
     * @param {string} action - Acción específica (ej: 'ver', 'crear') - opcional
     * @returns {boolean}
     */
    const hasModulePermission = (module, action = null) => {
        if (action) {
            return permissions.includes(`${module}_${action}`);
        }
        // Si no se especifica acción, verificar si tiene algún permiso del módulo
        return permissions.some(permission => permission.startsWith(`${module}_`));
    };

    /**
     * Obtiene todos los permisos de un módulo específico que tiene el usuario
     * @param {string} module - Nombre del módulo
     * @returns {string[]}
     */
    const getModulePermissions = (module) => {
        return permissions.filter(permission => permission.startsWith(`${module}_`));
    };

    /**
     * Verifica si el usuario es administrador (tiene todos los permisos básicos)
     * @returns {boolean}
     */
    const isAdmin = () => {
        const adminPermissions = [
            'usuarios_ver', 'usuarios_crear', 'usuarios_editar', 'usuarios_eliminar',
            'roles_ver', 'roles_crear', 'roles_editar', 'roles_eliminar'
        ];
        return hasAllPermissions(adminPermissions);
    };

    /**
     * Verifica si el usuario puede acceder a la gestión de usuarios
     * @returns {boolean}
     */
    const canManageUsers = () => {
        return hasPermission('usuarios_ver');
    };

    /**
     * Verifica si el usuario puede acceder a la gestión de roles
     * @returns {boolean}
     */
    const canManageRoles = () => {
        return hasPermission('roles_ver');
    };

    /**
     * Verifica si el usuario puede procesar ventas
     * @returns {boolean}
     */
    const canProcessSales = () => {
        return hasPermission('ventas_ver');
    };

    /**
     * Verifica si el usuario puede gestionar productos
     * @returns {boolean}
     */
    const canManageProducts = () => {
        return hasPermission('productos_ver');
    };

    /**
     * Verifica si el usuario puede acceder a reportes
     * @returns {boolean}
     */
    const canAccessReports = () => {
        return hasPermission('reportes_ventas_ver');
    };

    /**
     * Verifica si el usuario puede gestionar clientes
     * @returns {boolean}
     */
    const canManageClients = () => {
        return hasPermission('clientes_ver');
    };

    /**
     * Verifica si el usuario puede acceder a configuración
     * @returns {boolean}
     */
    const canAccessSettings = () => {
        return hasAnyPermission(['configuracion_ver', 'configuracion_admin']);
    };

    /**
     * Verifica si el usuario puede administrar configuraciones de empresa
     * @returns {boolean}
     */
    const canAdminSettings = () => {
        return hasPermission('configuracion_admin');
    };

    /**
     * Verifica si el usuario puede gestionar almacenes
     * @returns {boolean}
     */
    const canManageAlmacenes = () => {
        return hasPermission('almacenes_ver');
    };

    /**
     * Verifica si el usuario puede ver cuentas por pagar
     * @returns {boolean}
     */
    const canViewCXP = () => {
        return hasPermission('cxp_ver');
    };

    /**
     * Verifica si el usuario puede marcar cuentas como pagadas
     * @returns {boolean}
     */
    const canPayCXP = () => {
        return hasPermission('cxp_pagar');
    };

    /**
     * Verifica si el usuario puede ver cuentas por cobrar
     * @returns {boolean}
     */
    const canViewCXC = () => {
        return hasPermission('cxc_ver');
    };

    /**
     * Verifica si el usuario puede registrar pagos en CXC
     * @returns {boolean}
     */
    const canRegisterPaymentCXC = () => {
        return hasPermission('cxc_registrar_pago');
    };

    /**
     * Verifica si el usuario puede ver historial de pagos CXC
     * @returns {boolean}
     */
    const canViewHistorialCXC = () => {
        return hasPermission('cxc_ver_historial');
    };

    return {
        permissions,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        hasModulePermission,
        getModulePermissions,
        isAdmin,
        canManageUsers,
        canManageRoles,
        canProcessSales,
        canManageProducts,
        canAccessReports,
        canManageClients,
        canAccessSettings,
        canAdminSettings,
        canManageAlmacenes,
        canViewCXP,
        canPayCXP,
        canViewCXC,
        canRegisterPaymentCXC,
        canViewHistorialCXC
    };
};

export default usePermissions;